import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material'

import { getConfig } from '../../config'
import { useEscapeKey } from '../../hooks/useEscapeKey'

type TerminalPaymentStatus = 'PENDING' | 'PAID' | 'CANCELLED'

type SseEvent = {
  status: TerminalPaymentStatus
  receiptId?: string
}

type DialogStatus = 'PENDING' | 'CANCELLED' | 'TIMEOUT'

type Props = {
  paymentId: string
  open: boolean
  onPaid: (receiptId: string) => void
  onCancelled: () => void
}

const TIMEOUT_SECONDS = 60

export const TerminalPaymentWaitingDialog: FC<Props> = ({ paymentId, open, onPaid, onCancelled }) => {
  const { t } = useTranslation()
  const [secondsLeft, setSecondsLeft] = useState(TIMEOUT_SECONDS)
  const [status, setStatus] = useState<DialogStatus>('PENDING')
  const eventSourceRef = useRef<EventSource | null>(null)
  const onPaidRef = useRef(onPaid)
  const onCancelledRef = useRef(onCancelled)

  onPaidRef.current = onPaid
  onCancelledRef.current = onCancelled

  // No-op: this dialog is intentionally non-dismissable — the user must wait
  // for the terminal to complete or time out (max 60s + backend 65s safety timeout).
  useEscapeKey(() => {}, open)

  useEffect(() => {
    if (!open || !paymentId) return

    setStatus('PENDING')

    const apiBase = getConfig().BE_URL
    const es = new EventSource(`${apiBase}/terminal-payments/${paymentId}/stream`)
    eventSourceRef.current = es

    es.onmessage = (event) => {
      const data: SseEvent = JSON.parse(event.data)
      if (data.status === 'PAID') {
        es.close()
        onPaidRef.current(data.receiptId!)
      } else if (data.status === 'CANCELLED') {
        es.close()
        setStatus('CANCELLED')
        onCancelledRef.current()
      }
      // PENDING — keep counting down
    }

    es.onerror = () => {
      // EventSource reconnects automatically — no action needed
    }

    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        const next = Math.max(0, s - 1)
        if (next === 0) {
          // Frontend countdown exhausted; set TIMEOUT status so the UI reflects this.
          // The backend will send CANCELLED via SSE shortly (65s safety timeout),
          // which will call onCancelled() and close the dialog.
          setStatus('TIMEOUT')
        }
        return next
      })
    }, 1000)

    return () => {
      clearInterval(interval)
      es.close()
      eventSourceRef.current = null
      setSecondsLeft(TIMEOUT_SECONDS)
      setStatus('PENDING')
    }
  }, [open, paymentId])

  const progress = ((TIMEOUT_SECONDS - secondsLeft) / TIMEOUT_SECONDS) * 100

  const statusText =
    status === 'CANCELLED'
      ? t('terminalPayment.cancelled')
      : status === 'TIMEOUT'
        ? t('terminalPayment.timingOut')
        : t('terminalPayment.waiting')

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>{t('terminalPayment.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 3 }}>
          {status === 'PENDING' && (
            <>
              <CircularProgress size={64} />
              <Typography variant="body1">{t('terminalPayment.tapCard')}</Typography>
            </>
          )}
          <Box sx={{ width: '100%' }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
            >
              {t('terminalPayment.timeLeft', { seconds: secondsLeft })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {statusText}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
